import role21MfrsSchema from '../../../../assets/forms/role-720000-mfrs.form.json';
import role21MpersSchema from '../../../../assets/forms/role-720000-mpers.form.json';
import { MPERS_ACCOUNTING_STANDARD } from './schema-picker.util';

/**
 * Shared business rules for Role 21 (Notes - Summary of Material Accounting
 * Policies).
 *
 * Role 21 mirrors Role 20's accounting-standard-dependent schema pattern: the
 * sheet ships as two sibling form JSONs (role-720000-mfrs.form.json and
 * role-720000-mpers.form.json). Both schemas list the full superset of
 * material accounting-policy disclosures but differ in qname prefix for the
 * SSM-specific items (ssmt-mfrs:* vs ssmt-mpers:*) and in the note / label
 * copy that references "MFRS" vs "MPERS".
 *
 * Selection happens at mount time in the component based on
 * filingPrerequisites['ssmt-dei:BasisOfAccountingStandardsAppliedToPrepareFinancialStatements'];
 * the pure validator has to reproduce the same choice from
 * SheetValidationContext.accountingStandard so it validates against the same
 * required-field set the user saw on the screen.
 *
 * Both the editor's
 * role21-720000-notes-accounting-policies.component.ts AND
 * role21NotesAccountingPoliciesValidator import from this file so the
 * selection literal and schema imports live in exactly one place. See
 * role20-notes-corporate-information.rules.ts for the sibling implementation.
 */

/**
 * Literal value of
 * filingPrerequisites['ssmt-dei:BasisOfAccountingStandardsAppliedToPrepareFinancialStatements']
 * that selects the MPERS schema. Any other value (including undefined, '',
 * or 'Malaysian Financial Reporting Standards') falls back to MFRS, matching
 * the component's original `let schemaFile = 'role-720000-mfrs.form.json'`
 * default assignment.
 */
export const ROLE21_MPERS_ACCOUNTING_STANDARD = MPERS_ACCOUNTING_STANDARD;

/** Filename of the MFRS (default) variant schema. */
export const ROLE21_MFRS_SCHEMA_FILE = 'role-720000-mfrs.form.json';

/** Filename of the MPERS variant schema. */
export const ROLE21_MPERS_SCHEMA_FILE = 'role-720000-mpers.form.json';

/**
 * Narrow shape of a role 21 schema control used by both the component's
 * renderer and the pure validator. The schemas carry richer metadata (ui,
 * error, note, etc.) but the validator only needs qname, required, and the
 * validators array — keeping this type tight makes the validator resilient
 * to unrelated schema additions.
 */
export interface Role21SchemaControl {
  qname: string;
  required?: boolean;
  validators?: Array<{ name?: string }>;
}

export interface Role21Schema {
  role: string;
  sheetId: string;
  label: string;
  controls: Role21SchemaControl[];
}

/**
 * Resolve the correct role 21 schema filename for the given accounting
 * standard. Kept as a separate helper so the component's async fetch() and
 * the validator's static JSON import both derive their target from the same
 * selection rule.
 */
export function pickRole21SchemaFile(
  accountingStandard: string | undefined | null
): string {
  if (accountingStandard === ROLE21_MPERS_ACCOUNTING_STANDARD) {
    return ROLE21_MPERS_SCHEMA_FILE;
  }
  return ROLE21_MFRS_SCHEMA_FILE;
}

/**
 * Resolve the actual role 21 schema object for the given accounting standard.
 * Used by the pure validator which cannot fetch() at validate-time (the
 * listing/dashboard's cache-miss path runs synchronously) so both variants
 * are imported eagerly and selected from memory.
 */
export function pickRole21Schema(
  accountingStandard: string | undefined | null
): Role21Schema {
  if (accountingStandard === ROLE21_MPERS_ACCOUNTING_STANDARD) {
    return role21MpersSchema as unknown as Role21Schema;
  }
  return role21MfrsSchema as unknown as Role21Schema;
}
